import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ThumbsUp, ThumbsDown, MessageCircle, Send, User } from 'lucide-react';
import './HotTopics.css';

const HotTopics = React.memo(() => {
    // ✅ Cache management helper
    const clearAllCaches = useCallback(() => {
        console.log('🧹 Clearing all frontend caches...');
        localStorage.removeItem('hot-topics-cache');
        sessionStorage.clear();
        console.log('✅ Frontend caches cleared');
    }, []);

    // ✅ Optimized: Combined state management
    const [state, setState] = useState({
        topics: [],
        loading: true,
        error: null,
        lastUpdated: null,
        enhanced: false,
        newsMetrics: {},
        communityMetrics: {
            totalPredictions: 0,
            qualityFiltered: false,
            thresholds: {},
            aiProcessed: false,
            cacheHit: false
        }
    });

    // Community predictions state
    const [communityState, setCommunityState] = useState({
        currentIndex: 0,
        data: [],
        autoRotate: true
    });

    // News state  
    const [newsState, setNewsState] = useState({
        currentIndex: 0,
        data: [],
        autoRotate: true
    });

    const communityTimeoutRef = useRef(null);
    const communityInteractionTimeoutRef = useRef(null);
    const newsTimeoutRef = useRef(null);
    const newsInteractionTimeoutRef = useRef(null);
    const [isTypingComment, setIsTypingComment] = useState({});

    // Add these state variables to your HotTopics component
    // Removed - comments are now stored in communityState.data




const [expandedItems, setExpandedItems] = useState(new Set());
const [qualityLevel, setQualityLevel] = useState('high');
    // Authentication and interaction states
const [user, setUser] = useState(null);

// Add user authentication check on component mount
useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
        // Decode token or fetch user data
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            setUser({ 
                id: payload.id || payload.user_id, 
                username: payload.username || payload.name || 'User',
                email: payload.email 
            });
        } catch (error) {
            console.error('Error parsing token:', error);
            // Clear invalid token
            localStorage.removeItem('token');
            sessionStorage.removeItem('token');
        }
    }
}, []);
const [showLoginModal, setShowLoginModal] = useState(false);
const [expandedComments, setExpandedComments] = useState({});
const [commentText, setCommentText] = useState({});
const [submittingComment, setSubmittingComment] = useState({});
const [userInteractions, setUserInteractions] = useState({}); // Track user's likes/dislikes per prediction
const [interactionLoading, setInteractionLoading] = useState({}); // ✅ ADD THIS LINE





    // ✅ NEW: Bullet point formatting function
    const formatBulletPoints = useCallback((bulletPoints) => {
        if (!bulletPoints || !Array.isArray(bulletPoints)) return [];
        
        return bulletPoints
            .filter(point => point && typeof point === 'string' && point.trim().length > 0)
            .map(point => {
                // Clean up bullet point formatting
                let cleanPoint = point.trim();
                
                // Remove existing bullet symbols if any
                cleanPoint = cleanPoint.replace(/^[•\-\*\s]+/, '');
                
                // Ensure it starts with bullet symbol
                if (!cleanPoint.startsWith('•')) {
                    cleanPoint = `• ${cleanPoint}`;
                }
                
                return cleanPoint;
            })
            .slice(0, 4); // Maximum 4 bullet points
    }, []);

    // ✅ Optimized: Memoized utility functions
    // Function to ensure text wrapping and full visibility
    const ensureTextWrapping = useCallback((text, maxLength = 100) => {
        if (!text) return "";
        
        // Don't truncate - instead ensure proper spacing for wrapping
        const wrappedText = text
            .replace(/([a-z])([A-Z])/g, "$1 $2") // Add spaces between camelCase
            .replace(/([a-zA-Z])([0-9])/g, "$1 $2") // Add spaces between letters and numbers
            .replace(/([0-9])([a-zA-Z])/g, "$1 $2") // Add spaces between numbers and letters
            .replace(/\s+/g, " ") // Remove extra spaces
            .trim();
        
        return wrappedText;
    }, []);
    
    // Function to add word break opportunities
    const addWordBreaks = useCallback((text) => {
        if (!text) return "";
        
        // Add zero-width spaces for better breaking
        return text
            .replace(/(and|or|the|of|in|on|at|to|for|with|by)/gi, "$1\u200B") // Add break after common words
            .replace(/([a-z])([A-Z])/g, "$1\u200B$2") // Add break between camelCase
            .replace(/(\w{8,})/g, (match) => {
                // Add breaks in long words
                return match.replace(/(.{4})/g, "$1\u200B");
            });
    }, []);

    // ✅ NEW: Convert description to bullet points if needed
    const extractBulletPointsFromDescription = useCallback((description) => {
        if (!description) return [];
        
        // Check if description contains bullet points
        if (description.includes('•') || description.includes('\n')) {
            const points = description
                .split(/[•\n]/)
                .map(point => point.trim())
                .filter(point => point.length > 3)
                .slice(0, 4)
                .map(point => `• ${addWordBreaks(ensureTextWrapping(point))}`);
            
            return points.length > 0 ? points : [`• ${description}`];
        }
        
        // If no bullet points, create one from description
        return [`• ${description}`];
    }, [addWordBreaks, ensureTextWrapping]);

    const formatTitle = useCallback((title) => {
        if (!title) return 'Breaking News';
        
        console.log('🔧 Formatting title:', title);
        
        let formattedTitle = title
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
            .replace(/([a-z])([0-9])/g, '$1 $2')
            .replace(/([0-9])([a-z])/g, '$1 $2')
            .replace(/stocks/gi, ' Stocks')
            .replace(/market/gi, ' Market')
            .replace(/earnings/gi, ' Earnings')
            .replace(/predictions/gi, ' Predictions')
            .replace(/trends/gi, ' Trends')
            .replace(/intelligence/gi, ' Intelligence')
            .replace(/analysis/gi, ' Analysis')
            .replace(/community/gi, ' Community')
            .replace(/photos/gi, ' Photos')
            .replace(/model/gi, ' Model')
            .replace(/turns/gi, ' Turns')
            .replace(/into/gi, ' Into')
            .replace(/inside/gi, ' Inside')
            .replace(/plans/gi, ' Plans')
            .replace(/make/gi, ' Make')
            .replace(/beat/gi, ' Beat')
            .replace(/shares/gi, ' Shares')
            .replace(/\s+/g, ' ')
            .trim();
        
        if (formattedTitle.length > 30 && !formattedTitle.includes(' ')) {
            formattedTitle = formattedTitle
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/(\w{4,})(\w{4,})/g, '$1 $2');
        }
        
        formattedTitle = formattedTitle.charAt(0).toUpperCase() + formattedTitle.slice(1);
        formattedTitle = formattedTitle.replace(/^\s+|\s+$/g, '').replace(/\s+/g, ' ');
        
        console.log('✅ Formatted title result:', formattedTitle);
        return formattedTitle || 'Breaking News';
    }, []);

    const getNewsAgency = useCallback((topic) => {
        console.log('🏢 Getting news agency for topic:', topic);
        
        if (topic.articles && topic.articles.length > 0) {
            const firstArticle = topic.articles[0];
            if (firstArticle.source?.name && firstArticle.source.name !== 'NewsAPI') {
                console.log('📰 Found article source:', firstArticle.source.name);
                return firstArticle.source.name;
            }
            if (firstArticle.source && typeof firstArticle.source === 'string' && firstArticle.source !== 'NewsAPI') {
                console.log('📰 Found string source:', firstArticle.source);
                return firstArticle.source;
            }
        }
        
        if (topic.source && 
            topic.source !== 'NewsAPI + AI Analysis' && 
            topic.source !== 'NewsAPI' && 
            topic.source !== 'Live News' &&
            topic.source.trim() !== '') {
            console.log('📰 Found topic source:', topic.source);
            return topic.source;
        }
        
        if (topic.newsContext?.primarySource && topic.newsContext.primarySource !== 'NewsAPI') {
            console.log('📰 Found context source:', topic.newsContext.primarySource);
            return topic.newsContext.primarySource;
        }
        
        if (topic.category && topic.category !== 'News' && topic.category !== 'Breaking News') {
            console.log('📰 Using category as source:', topic.category);
            return topic.category;
        }
        
        console.log('📰 Using fallback source');
        return 'Global News Sources';
    }, []);

    // ✅ Updated functions to return null (no icons)
    const getTrendIcon = useCallback(() => null, []);
    const getCategoryIcon = useCallback(() => null, []);

    // ✅ NEW: Local storage cache utilities
    const CACHE_KEY = 'hot-topics-cache';
    const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

    const getCachedData = useCallback(() => {
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) {
                const { data, timestamp } = JSON.parse(cached);
                const isValid = Date.now() - timestamp < CACHE_DURATION;
                return isValid ? data : null;
            }
        } catch (error) {
            console.warn('Cache read error:', error);
        }
        return null;
    }, []);

    const setCachedData = useCallback((data) => {
        try {
            localStorage.setItem(CACHE_KEY, JSON.stringify({
                data,
                timestamp: Date.now()
            }));
        } catch (error) {
            console.warn('Cache write error:', error);
        }
    }, []);

    const formatTimeAgo = useCallback((timestamp) => {
        if (!timestamp) return '';
        const now = new Date();
        const time = new Date(timestamp);
        const diffInMinutes = Math.floor((now - time) / (1000 * 60));
        
        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
        return `${Math.floor(diffInMinutes / 1440)}d ago`;
    }, []);

const fetchCommunityPredictions = useCallback(async () => {
    try {
        console.log('🔍 Fetching community predictions via 72-hour cache system...');
        console.log("🔍 API URL:", process.env.REACT_APP_API_URL);
        console.log("🔍 Full URL:", `/api/hot-topics/community`);

        const response = await fetch(`/api/hot-topics/ai`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Community predictions API error: ${response.status}`);
        }

        // ✅ Parse the JSON response
const data = await response.json();
console.log('✅ AI Hot Topics response:', data);

// ✅ FIX: Get topics from correct path (backend returns 'topics', not 'predictions')
const predictions = data.topics || [];

        console.log('🔍 Raw predictions from API:', predictions.length, 'total');
        console.log('🔍 Data structure:', JSON.stringify(data, null, 2));

        if (predictions.length > 0) {
            console.log('🔍 First prediction sample:', predictions[0]);
        }

        // ✅ FIX: Backend returns predictionText, not description
        const validPredictions = predictions.filter(pred => {
            // ✅ CRITICAL: Filter out test data
            const isTestData = 
                pred.id === 163 || 
                pred.predictionId === 163 ||
                (pred.predictionText && (
                    pred.predictionText.toLowerCase().includes('test') ||
                    pred.predictionText.toLowerCase().includes('debug')
                ));

            if (isTestData) {
                console.warn('🚨 FILTERED OUT TEST PREDICTION:', pred.id);
                return false;
            }

            // Only check for required fields
            const isValid = pred.title && pred.predictionText;
            if (!isValid) {
                console.log('⚠️ Filtered out prediction (missing title/predictionText):', pred.id);
            }
            return isValid;
        });

        console.log(`✅ Valid predictions: ${validPredictions.length} of ${predictions.length}`);


        // ✅ Update community metrics from backend response
        setState(prev => ({
            ...prev,
            communityMetrics: {
                totalPredictions: data.metadata?.count || predictions.length,
                qualityFiltered: data.metadata?.qualityFiltered || true,
                thresholds: data.metadata?.thresholds || {
                    predictionReliability: 70,
                    userReliability: 40
                },
                cacheHit: data.metadata?.cached || false,
                lastUpdated: data.metadata?.generated || new Date().toISOString(),
                
                // Calculate engagement from predictions array
                totalLikes: predictions.reduce((sum, p) => sum + (p.likes_count || 0), 0),
                totalDislikes: predictions.reduce((sum, p) => sum + (p.dislikes_count || 0), 0),
                totalComments: predictions.reduce((sum, p) => sum + (p.comments_count || 0), 0),
                totalEngagement: predictions.reduce((sum, p) => 
                    sum + (p.likes_count || 0) + (p.dislikes_count || 0) + (p.comments_count || 0), 0
                ),
                activeUsers: new Set(predictions.map(p => p.username)).size,
                averageEngagement: predictions.length > 0 
                    ? predictions.reduce((sum, p) => 
                        sum + (p.likes_count || 0) + (p.dislikes_count || 0) + (p.comments_count || 0), 0
                    ) / predictions.length
                    : 0,
                aiProcessed: true,
                processingTime: data.metadata?.cached ? 'Cached' : 'Fresh'
            }
        }));


        // ✅ Transform backend structure to frontend format
        console.log('🔍 Raw predictions before mapping:', predictions);
        console.log('🔍 Predictions length:', predictions.length);

// ✅ Use validPredictions from the filter above
console.log(`🎯 Quality Filter: ${predictions.length} total → ${validPredictions.length} valid`);
console.log('🔍 First prediction before filter:', predictions[0]);
console.log('🔍 First valid prediction:', validPredictions[0]);
console.log('🔍 Is this fallback data?', typeof predictions[0]?.id === 'string' && predictions[0]?.id?.includes('fallback'));

if (validPredictions.length === 0) {
    console.warn('⚠️ No predictions have both title and description');
}

const communityTopics = validPredictions.map((pred, index) => {
            // ✅ CRITICAL FIX: Extract numeric ID for like/dislike interactions
// ✅ SIMPLIFIED: Backend now returns clean predictionId
const numericId = pred.predictionId || pred.id || (999900 + index);

console.log(`🆔 Prediction ID:`, {
    original: pred.id,
    predictionId: pred.predictionId,
    final: numericId,
    type: typeof numericId
});


            console.log(`🆔 Final ID:`, {
                original: pred.id,
                final: numericId,
                type: typeof numericId,
                valid: typeof numericId === 'number' && !isNaN(numericId)
            });

// ✅ FIXED: Backend returns predictionText, not description
const aiGeneratedTitle = pred.title; // AI-generated title from backend
const originalPrediction = pred.predictionText; // The actual prediction text

// Use AI title if available, otherwise create one
const displayTitle = aiGeneratedTitle || formatTitle(originalPrediction || 'Untitled Prediction');

// Check if we have separate AI analysis
const hasAiAnalysis = pred.aiAnalysis && pred.aiAnalysis !== originalPrediction;
const previewText = originalPrediction; // Show original prediction as preview
const fullAnalysisText = pred.aiAnalysis || pred.communityInsight || originalPrediction; // AI analysis if available


console.log(`📝 Topic ${index}:`, {
    id: numericId,
    title: displayTitle,
    isAIGenerated: pred.aiGenerated,
    hasAiAnalysis,
    qualityScore: Math.max(pred.reliabilityScore || 0, pred.overall_reliability_score || 0),
    previewLength: previewText?.length || 0,
    analysisLength: fullAnalysisText?.length || 0,
    commentsCount: pred.comments_count || 0,
    likesCount: pred.likes_count || 0,
    dislikesCount: pred.dislikes_count || 0
});

return {
    id: numericId,
    originalId: pred.id,
    category: pred.category || 'Community Intelligence',
    title: pred.title || 'Untitled Prediction',  // ← AI-generated title from backend
    description: originalPrediction || '',  // ← Use the variable we created above
    source: 'Community',
    type: 'community',
    trend: pred.trend || 'stable',
    
    likes_count: parseInt(pred.engagement?.likes) || 0,
    dislikes_count: parseInt(pred.engagement?.dislikes) || 0,
    comments_count: parseInt(pred.engagement?.comments) || 0,

    engagement_score: pred.engagement_score || 0,
    comments: Array.isArray(pred.comments) ? pred.comments.map(c => ({
        id: c.id,
        text: c.comment_text || c.text,
        username: c.username,
        created_at: c.created_at || c.timestamp,
        user_id: c.user_id
    })) : [],
    
userReliability: parseFloat(pred.userReliability) || 0,
predictionReliability: parseFloat(pred.reliabilityScore) || 0,
overallReliability: parseFloat(pred.reliabilityScore) || 0,
qualityScore: parseFloat(pred.reliabilityScore) || 0,

    qualityRank: pred.qualityRank,
    avgRating: pred.avgRating,
    qualityMet: true,
    
    username: pred.username,
    email: pred.email,
    user_id: pred.user_id,
    
    target_date: pred.target_date,
    created_at: pred.created_at,
    updated_at: pred.updated_at,
    timestamp: pred.timestamp,
    
    tags: pred.tags,
    keywords: pred.keywords || [],
    
    communityInsight: fullAnalysisText || previewText,
    hasAiSummary: pred.aiGenerated || false,
    aiGenerated: pred.aiGenerated || false,
    
    hasSeparateContent: hasAiAnalysis,
    previewDescription: previewText,
    fullAnalysis: fullAnalysisText
};
});

console.log('👥 Processed community topics:', communityTopics.length);
console.log('🤖 Topics with AI titles:', communityTopics.filter(t => t.aiGenerated).length);
console.log('🤖 Topics with AI analysis:', communityTopics.filter(t => t.hasSeparateContent).length);
console.log('🏆 Quality scores:', communityTopics.map(t => t.qualityScore));
console.log('🆔 Final IDs:', communityTopics.map(t => ({ original: t.originalId, final: t.id })));
console.log('💬 Comments data:', communityTopics.map(t => ({ id: t.id, commentsCount: t.comments_count, actualComments: t.comments?.length || 0 })));

if (communityTopics.length === 0) {
    console.warn('⚠️ No predictions meet quality > 80 threshold');
}

return communityTopics;


    } catch (error) {
        console.error('❌ Error fetching community predictions:', error);
        
        return [
            {
                id: 999901, // ✅ Numeric fallback ID
                originalId: 'fallback-1',
                category: 'Technology',
                title: 'Tesla Achieves Full Autonomous Driving Certification',
                description: 'Tesla will achieve Level 5 autonomous driving certification by December 2025, marking a revolutionary milestone in transportation technology.',
                source: 'Community',
                type: 'community',
                trend: 'trending',
                confidence: 88,
                qualityScore: 88,
                predictionCount: 47,
                likes_count: 23,
                dislikes_count: 4,
                comments_count: 12,
                engagement_score: 39,
                qualityMet: true,
                hasAiSummary: true,
                aiGenerated: true,
                hasSeparateContent: true,
                previewDescription: 'Tesla will achieve Level 5 autonomous driving certification by December 2025, marking a revolutionary milestone in transportation technology.',
                fullAnalysis: 'This prediction demonstrates strong technical foundations with Tesla\'s continuous FSD improvements and regulatory momentum. Key success factors include: neural network advancement showing exponential learning curves, safety validation protocols being refined through millions of real-world miles, and favorable regulatory environment with NHTSA guidelines becoming clearer.',
                communityInsight: 'This prediction demonstrates strong technical foundations with Tesla\'s continuous FSD improvements and regulatory momentum. Key success factors include: neural network advancement showing exponential learning curves, safety validation protocols being refined through millions of real-world miles, and favorable regulatory environment with NHTSA guidelines becoming clearer.'
            },
            {
                id: 999902,
                originalId: 'fallback-2',
                category: 'Finance',
                title: 'Bitcoin Surpasses $150K Milestone in 2025',
                description: 'Bitcoin will surpass $150,000 by mid-2025, driven by institutional adoption and favorable regulatory clarity.',
                source: 'Community',
                type: 'community',
                trend: 'rising',
                confidence: 85,
                qualityScore: 85,
                predictionCount: 38,
                likes_count: 19,
                dislikes_count: 3,
                comments_count: 8,
                engagement_score: 30,
                qualityMet: true,
                hasAiSummary: true,
                aiGenerated: true,
                hasSeparateContent: true,
                previewDescription: 'Bitcoin will surpass $150,000 by mid-2025, driven by institutional adoption and favorable regulatory clarity.',
                fullAnalysis: 'This prediction shows 85% reliability based on institutional adoption trends and regulatory developments. Key catalysts include: corporate treasury adoption accelerating with major companies adding Bitcoin to balance sheets, ETF inflows providing sustained demand pressure, and monetary policy impacts as central banks maintain accommodative stances.',
                communityInsight: 'This prediction shows 85% reliability based on institutional adoption trends and regulatory developments. Key catalysts include: corporate treasury adoption accelerating with major companies adding Bitcoin to balance sheets, ETF inflows providing sustained demand pressure, and monetary policy impacts as central banks maintain accommodative stances.'
            }
        ];
    }
}, [formatTitle]);



// ✅ Optimized: Combined rotation logic
useEffect(() => {
    const hasActiveComment = Object.values(isTypingComment).some(typing => typing);
    
    if (communityState.autoRotate && communityState.data.length > 1 && !hasActiveComment) {
        communityTimeoutRef.current = setTimeout(() => {
            setCommunityState(prev => ({
                ...prev,
                currentIndex: (prev.currentIndex + 1) % prev.data.length
            }));
        }, 4000);
    }
    return () => clearTimeout(communityTimeoutRef.current);
}, [communityState.currentIndex, communityState.autoRotate, communityState.data.length, isTypingComment]);

useEffect(() => {
    if (newsState.autoRotate && newsState.data.length > 1) {
        newsTimeoutRef.current = setTimeout(() => {
            setNewsState(prev => ({
                ...prev,
                currentIndex: (prev.currentIndex + 1) % prev.data.length
            }));
        }, 6000);
    }
    return () => clearTimeout(newsTimeoutRef.current);
}, [newsState.currentIndex, newsState.autoRotate, newsState.data.length]);

// ✅ Optimized: Navigation handlers
const handleCommunityPrevious = useCallback(() => {
    setCommunityState(prev => ({ ...prev, autoRotate: false }));
    clearTimeout(communityTimeoutRef.current);
    clearTimeout(communityInteractionTimeoutRef.current);
    
    setCommunityState(prev => ({
        ...prev,
        currentIndex: prev.currentIndex === 0 ? prev.data.length - 1 : prev.currentIndex - 1
    }));
    
    communityInteractionTimeoutRef.current = setTimeout(() => {
        setCommunityState(prev => ({ ...prev, autoRotate: true }));
    }, 5000);
}, []);

const handleCommunityNext = useCallback(() => {
    setCommunityState(prev => ({ ...prev, autoRotate: false }));
    clearTimeout(communityTimeoutRef.current);
    clearTimeout(communityInteractionTimeoutRef.current);
    
    setCommunityState(prev => ({
        ...prev,
        currentIndex: (prev.currentIndex + 1) % prev.data.length
    }));
    
    communityInteractionTimeoutRef.current = setTimeout(() => {
        setCommunityState(prev => ({ ...prev, autoRotate: true }));
    }, 5000);
}, []);

// ✅ NEW: Pause rotation on hover (Community)
const handleCommunityMouseEnter = useCallback(() => {
    setCommunityState(prev => ({ ...prev, autoRotate: false }));
    clearTimeout(communityTimeoutRef.current);
    clearTimeout(communityInteractionTimeoutRef.current);
}, []);

const handleCommunityMouseLeave = useCallback(() => {
    clearTimeout(communityInteractionTimeoutRef.current);
    communityInteractionTimeoutRef.current = setTimeout(() => {
        setCommunityState(prev => ({ ...prev, autoRotate: true }));
    }, 5000);
}, []);

const handleCommunityDotClick = useCallback((index) => {
    setCommunityState(prev => ({ ...prev, autoRotate: false }));
    clearTimeout(communityTimeoutRef.current);
    clearTimeout(communityInteractionTimeoutRef.current);
    
    setCommunityState(prev => ({ ...prev, currentIndex: index }));
    
    communityInteractionTimeoutRef.current = setTimeout(() => {
        setCommunityState(prev => ({ ...prev, autoRotate: true }));
    }, 8000);
}, []);

const handleNewsPrevious = useCallback(() => {
    setNewsState(prev => ({ ...prev, autoRotate: false }));
    clearTimeout(newsTimeoutRef.current);
    clearTimeout(newsInteractionTimeoutRef.current);
    
    setNewsState(prev => ({
        ...prev,
        currentIndex: prev.currentIndex === 0 ? prev.data.length - 1 : prev.currentIndex - 1
    }));
    
    newsInteractionTimeoutRef.current = setTimeout(() => {
        setNewsState(prev => ({ ...prev, autoRotate: true }));
    }, 8000);
}, []);

const handleNewsNext = useCallback(() => {
    setNewsState(prev => ({ ...prev, autoRotate: false }));
    clearTimeout(newsTimeoutRef.current);
    clearTimeout(newsInteractionTimeoutRef.current);
    
    setNewsState(prev => ({
        ...prev,
        currentIndex: (prev.currentIndex + 1) % prev.data.length
    }));
    
    newsInteractionTimeoutRef.current = setTimeout(() => {
        setNewsState(prev => ({ ...prev, autoRotate: true }));
    }, 8000);
}, []);

// ✅ NEW: Pause rotation on hover (News)
const handleNewsMouseEnter = useCallback(() => {
    setNewsState(prev => ({ ...prev, autoRotate: false }));
    clearTimeout(newsTimeoutRef.current);
    clearTimeout(newsInteractionTimeoutRef.current);
}, []);

const handleNewsMouseLeave = useCallback(() => {
    clearTimeout(newsInteractionTimeoutRef.current);
    newsInteractionTimeoutRef.current = setTimeout(() => {
        setNewsState(prev => ({ ...prev, autoRotate: true }));
    }, 8000);
}, []);

const handleNewsDotClick = useCallback((index) => {
    setNewsState(prev => ({ ...prev, autoRotate: false }));
    clearTimeout(newsTimeoutRef.current);
    clearTimeout(newsInteractionTimeoutRef.current);
    
    setNewsState(prev => ({ ...prev, currentIndex: index }));
    
    newsInteractionTimeoutRef.current = setTimeout(() => {
        setNewsState(prev => ({ ...prev, autoRotate: true }));
    }, 10000);
}, []);


// Updated Like handler with loading state
const handleLike = useCallback(async (predictionId) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    // Add these debug lines:
    console.log('🔍 DEBUG - Token from localStorage:', localStorage.getItem('token'));
    console.log('🔍 DEBUG - Token from sessionStorage:', sessionStorage.getItem('token'));
    console.log('🔍 DEBUG - Final token value:', token);
    console.log('🔍 DEBUG - Token is truthy?:', !!token);
    
    if (!token) {
        console.log('❌ No auth token found, showing login modal');
        setShowLoginModal(true);
        return;
    }
    
    // ✅ Set loading state
    setInteractionLoading(prev => ({ ...prev, [predictionId]: true }));
    
    try {
        console.log(`🔥 Attempting to like prediction: ${predictionId}`);
        console.log('🔑 Token exists:', !!token);
        
        const response = await fetch(`/api/predictions/${predictionId}/like`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        
        console.log('👍 Like response:', response.status, response.statusText);

        let responseData;
        try {
            responseData = await response.json();
            console.log('👍 Like response data:', responseData);
        } catch (parseError) {
            console.error('❌ Failed to parse like response:', parseError);
            const textResponse = await response.text();
            console.error('📄 Raw like response:', textResponse);
            alert('Server error: Invalid response format');
            return;
        }
        
        if (response.ok) {
            // Update local state with server response
            setCommunityState(prev => ({
                ...prev,
                data: prev.data.map(item => 
                    item.id === predictionId 
                        ? { 
                            ...item, 
                            likes_count: responseData.likes_count,
                            dislikes_count: responseData.dislikes_count
                        }
                        : item
                )
            }));
            
            // Update user interactions to track current state
            setUserInteractions(prev => ({
                ...prev,
                [predictionId]: responseData.user_action // 'liked', 'none', etc.
            }));
            
            console.log(`✅ Successfully ${responseData.user_action === 'liked' ? 'liked' : 'removed like from'} prediction ${predictionId}`);
        } else {
            console.error('Like failed:', responseData.message);
            alert(responseData.message || 'Failed to like prediction');
        }
    } catch (error) {
        console.error('Error liking prediction:', error);
        alert('Network error while liking prediction');
    } finally {
        // ✅ Clear loading state
        setInteractionLoading(prev => ({ ...prev, [predictionId]: false }));
    }
}, []);

// Dislike handler with loading state
const handleDislike = useCallback(async (predictionId) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    if (!token) {
        console.log('❌ No auth token found, showing login modal');
        setShowLoginModal(true);
        return;
    }
    
    // ✅ Set loading state
    setInteractionLoading(prev => ({ ...prev, [predictionId]: true }));
    
    try {
        console.log(`👎 Attempting to dislike prediction: ${predictionId}`);
        console.log('🔑 Token exists:', !!token);
        
        const response = await fetch(`/api/predictions/${predictionId}/dislike`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        
        console.log('👎 Dislike response:', response.status, response.statusText);

        let responseData;
        try {
            responseData = await response.json();
            console.log('👎 Dislike response data:', responseData);
        } catch (parseError) {
            console.error('❌ Failed to parse dislike response:', parseError);
            const textResponse = await response.text();
            console.error('📄 Raw dislike response:', textResponse);
            alert('Server error: Invalid response format');
            return;
        }
        
        if (response.ok) {
            // Update local state with server response
            setCommunityState(prev => ({
                ...prev,
                data: prev.data.map(item => 
                    item.id === predictionId 
                        ? { 
                            ...item, 
                            likes_count: responseData.likes_count,
                            dislikes_count: responseData.dislikes_count
                        }
                        : item
                )
            }));
            
            // Update user interactions to track current state
            setUserInteractions(prev => ({
                ...prev,
                [predictionId]: responseData.user_action // 'disliked', 'none', etc.
            }));
            
            console.log(`✅ Successfully ${responseData.user_action === 'disliked' ? 'disliked' : 'removed dislike from'} prediction ${predictionId}`);
        } else {
            console.error('Dislike failed:', responseData.message);
            alert(responseData.message || 'Failed to dislike prediction');
        }
    } catch (error) {
        console.error('Error disliking prediction:', error);
        alert('Network error while disliking prediction');
    } finally {
        // ✅ Clear loading state
        setInteractionLoading(prev => ({ ...prev, [predictionId]: false }));
    }
}, []);


const handleCommentToggle = useCallback(async (predictionId) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    if (!token) {
        setShowLoginModal(true);
        return;
    }
    
    const isCurrentlyExpanded = expandedComments[predictionId];
    
    setExpandedComments(prev => ({
        ...prev,
        [predictionId]: !isCurrentlyExpanded
    }));
    
    setIsTypingComment(prev => ({
        ...prev,
        [predictionId]: !isCurrentlyExpanded
    }));
    
    // ✅ NEW: Fetch comments if expanding and not already loaded
    if (!isCurrentlyExpanded) {
        const prediction = communityState.data.find(p => p.id === predictionId);
        if (!prediction?.comments || prediction.comments.length === 0) {
            try {
                const response = await fetch(`/api/predictions/${predictionId}/comments`);
                if (response.ok) {
                    const data = await response.json();
                    
                    // Update the prediction with fetched comments
                    setCommunityState(prev => ({
                        ...prev,
                        data: prev.data.map(item => 
                            item.id === predictionId 
                                ? { ...item, comments: data.comments || [] }
                                : item
                        )
                    }));
                    console.log(`✅ Loaded ${data.comments?.length || 0} comments for prediction ${predictionId}`);
                }
            } catch (error) {
                console.error('❌ Error fetching comments:', error);
            }
        }
    }
}, [expandedComments, communityState.data]);



const handleCommentSubmit = useCallback(async (predictionId) => {
    const comment = commentText[predictionId]?.trim();
    if (!comment) return;
    
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    if (!token) {
        setShowLoginModal(true);
        return;
    }
    
    setSubmittingComment(prev => ({ ...prev, [predictionId]: true }));
    
    try {
        const response = await fetch(`/api/predictions/${predictionId}/comments`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ comment: comment })
        });
        
        console.log('💬 Comment response:', response.status, response.statusText);

        if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ Comment failed:', errorText);
            alert('Failed to add comment');
            return;
        }

        const responseData = await response.json();
        console.log('💬 Comment response data:', responseData);

        // Clear input
        setCommentText(prev => ({ ...prev, [predictionId]: '' }));
        
        // ✅ NEW: Refetch comments to show the new one
        const commentsResponse = await fetch(`/api/predictions/${predictionId}/comments`);
        if (commentsResponse.ok) {
            const commentsData = await commentsResponse.json();
            
            setCommunityState(prev => ({
                ...prev,
                data: prev.data.map(item => 
                    item.id === predictionId 
                        ? { 
                            ...item, 
                            comments_count: responseData.comments_count || (item.comments_count || 0) + 1,
                            comments: commentsData.comments || []
                        }
                        : item
                )
            }));
            
            console.log(`✅ Successfully added comment and refreshed list for prediction ${predictionId}`);
        }
    } catch (error) {
        console.error('Error submitting comment:', error);
        alert('Network error while submitting comment');
    } finally {
        setSubmittingComment(prev => ({ ...prev, [predictionId]: false }));
    }
}, [commentText, user]);



// ✅ Base API URL (defaults to /api when env not set)
const API_BASE = process.env.REACT_APP_API_URL || '/api';

// ✅ Your existing fetchHotTopics with instant cache loading
const fetchHotTopics = useCallback(async () => {
    // ✅ 1. Check for cached data and show immediately
    const cachedData = getCachedData();
    if (cachedData) {
        console.log('📦 Loading from cache immediately...');
        
        // Set cached data immediately
        setCommunityState(prev => ({ ...prev, data: cachedData.communityTopics || [] }));
        setNewsState(prev => ({ ...prev, data: cachedData.newsItems || [] }));
        
        setState(prev => ({
            ...prev,
            topics: cachedData.allTopics || [],
            loading: false, // ✅ Stop loading immediately
            enhanced: cachedData.enhanced || false,
            newsMetrics: cachedData.newsMetrics || {},
            communityMetrics: cachedData.communityMetrics || {},
            lastUpdated: cachedData.lastUpdated
        }));
    } else {
        // Only show loading if no cache
        setState(prev => ({ ...prev, loading: true, error: null }));
    }

    try {
        console.log('🔄 Fetching fresh data in background...');
        
        // ✅ 2. Fetch fresh data with validation
        const [communityTopics, enhancedResponse] = await Promise.all([
            fetchCommunityPredictions(),
            fetch(`${API_BASE}/enhanced-hot-topics`).then(res => 
                res.ok ? res.json() : Promise.reject(new Error('Enhanced API failed'))
            ).catch(async (error) => {
                console.warn('Enhanced API failed, trying fallback:', error);
                const fallbackResponse = await fetch(`${API_BASE}/hot-topics`);
                return fallbackResponse.ok ? fallbackResponse.json() : Promise.reject(error);
            })
        ]);

        // Process enhanced hot topics response
        const responseData = enhancedResponse.data?.data || enhancedResponse.data || enhancedResponse;
        const allTopics = responseData.topics || [];
        
        // ✅ CRITICAL: Validate no test data exists
        const testTopics = allTopics.filter(topic => 
            topic.id === 163 || 
            topic.id === 'ai-topic-163' ||
            topic.predictionId === 163 ||
            (topic.predictionText && (
                topic.predictionText.toLowerCase().includes('test') ||
                topic.predictionText.toLowerCase().includes('debug')
            ))
        );

        if (testTopics.length > 0) {
            console.error('🚨 STALE DATA DETECTED IN API RESPONSE:', testTopics);
            clearAllCaches();
            throw new Error('Stale data detected - backend cache needs clearing');
        }

        console.log('📊 All topics found:', allTopics.length);
        console.log('🔍 Sample topic data:', allTopics[0]);
        
        setState(prev => ({
            ...prev,
            topics: allTopics,
            lastUpdated: responseData.timestamp,
            enhanced: enhancedResponse.enhanced !== false,
            newsMetrics: responseData.newsMetrics || enhancedResponse.data?.newsMetrics || {}
        }));

        // Set community data from service
        // Set community data from service
        // Set community data from service
        setCommunityState(prev => ({ ...prev, data: communityTopics }));


        // Process NewsAPI Topics (Breaking News, Top Stories, Trending, World Updates)
        const newsApiTopics = allTopics.filter(topic => 
            topic.type === 'newsapi' ||
            topic.type === 'news' ||
            topic.type === 'breaking' ||
            !topic.type || 
            (topic.type !== 'user_predictions' && topic.type !== 'community')
        );

        console.log('📰 News API topics found:', newsApiTopics.length);

        // Transform news topics for display with bullet points
        const newsItems = newsApiTopics.map((topic, index) => {
            const formattedTitle = formatTitle(topic.title);
            const newsSource = getNewsAgency(topic);
            
            // ✅ NEW: Process bullet points from API response
            const bulletPoints = topic.bulletPoints ? 
                formatBulletPoints(topic.bulletPoints) : 
                extractBulletPointsFromDescription(topic.description || topic.summary);
            
            console.log(`📰 News item ${index}:`, {
                original: topic.title,
                formatted: formattedTitle,
                source: newsSource,
                articles: topic.articles?.length || 0,
                bulletPoints: bulletPoints.length
            });
            
            return {
                id: topic.id || `news-${index}`,
                title: formattedTitle,
                description: topic.description || topic.summary || 'Latest news analysis powered by AI',
                bulletPoints: bulletPoints, // ✅ NEW: Add bullet points
                category: topic.category || 'Breaking News',
                source: newsSource,
                trend: topic.trend || 'breaking',
                articles: topic.articles || [],
                newsContext: topic.newsContext || {},
                keywords: topic.keywords || [],
                publishedAt: topic.publishedAt
            };
        });

        console.log('📰 Final processed news items:', newsItems);
        console.log('👥 Final processed community items:', communityTopics);

        // ✅ 3. DO NOT CACHE (temporarily disabled to prevent stale data)
        console.log('⚠️ Caching temporarily disabled to prevent stale data');
        
        const freshData = {
            communityTopics,
            newsItems,
            allTopics,
            enhanced: enhancedResponse.enhanced !== false,
            newsMetrics: responseData.newsMetrics || enhancedResponse.data?.newsMetrics || {},
            communityMetrics: {
                totalPredictions: communityTopics.length,
qualityFiltered: true,
predictionThreshold: 70,  // Backend uses reliability_score > 70
userThreshold: 40,  // Backend uses user_reliability > 40

                aiProcessed: true,
                cacheHit: false
            },
            lastUpdated: responseData.timestamp || new Date().toISOString()
        };

        // Cache disabled - DO NOT cache until stale data issue resolved
        // setCachedData(freshData);

        // Update state with fresh data
        setCommunityState(prev => ({ ...prev, data: communityTopics }));
        setNewsState(prev => ({ ...prev, data: newsItems }));

        setState(prev => ({
            ...prev,
            topics: allTopics,
            loading: false,
            enhanced: freshData.enhanced,
            newsMetrics: freshData.newsMetrics,
            communityMetrics: freshData.communityMetrics,
            lastUpdated: freshData.lastUpdated
        }));

        console.log('✅ Fresh data loaded and cached');

    } catch (err) {
        console.error('❌ Error loading hot topics:', err);
        
        // Only show error if we don't have cached data
        if (!cachedData) {
            setState(prev => ({ 
                ...prev, 
                error: 'Unable to load hot topics',
                loading: false 
            }));
        } else {
            console.log('📦 Continuing with cached data due to fetch error');
        }
    }
}, [fetchCommunityPredictions, formatTitle, getNewsAgency, formatBulletPoints, extractBulletPointsFromDescription, getCachedData, setCachedData]);
useEffect(() => {
    fetchHotTopics();
}, [fetchHotTopics]);




const fetchUserInteractions = useCallback(async (predictionIds) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token || predictionIds.length === 0) return {};
    
    try {
        const response = await fetch('/api/predictions/interactions', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ predictionIds })
        });
        
        if (response.ok) {
            const data = await response.json();
            console.log('📊 Interactions API response:', data);
            
            // ✅ Backend returns interactions as an object: { "123": "liked", "456": "disliked" }
            if (data.success && data.interactions && typeof data.interactions === 'object') {
                return data.interactions;
            }
        } else {
            console.warn('⚠️ Interactions API failed:', response.status);
        }
    } catch (error) {
        console.error('Error fetching user interactions:', error);
    }
    return {};
}, []);


// ✅ Simplified useEffect - fetch interactions when predictions load
useEffect(() => {
    const fetchInitialData = async () => {
        if (communityState.data.length === 0) return;
        
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) return;

        try {
            const predictionIds = communityState.data.map(pred => pred.id);
            const interactions = await fetchUserInteractions(predictionIds);
            setUserInteractions(interactions);
            console.log('✅ Interaction data loaded:', interactions);
        } catch (error) {
            console.error('❌ Error fetching interaction data:', error);
        }
    };

    fetchInitialData();
}, [communityState.data, fetchUserInteractions]);

// LoginModal component
const LoginModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;



    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Login Required</h3>
                    <button className="close-button" onClick={onClose}>&times;</button>
                </div>
                <div className="modal-body">
                    <p>Please log in to interact with community predictions.</p>
                    <div className="modal-actions">
                        <button 
                            className="login-btn"
                            onClick={() => {
                                window.location.href = '/login';
                            }}
                        >
                            Go to Login
                        </button>
                        <button className="cancel-btn" onClick={onClose}>
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


    // ✅ Loading state with updated messaging
    if (state.loading) {
        return (
            <section className="hot-topics-section">
                <div className="container">

                    <div className="hot-topics-header">
                        <h2 className="section-title">
                            Hot Topics
                        </h2>
                        <p className="section-subtitle">
                            "AI-powered trend analysis with community predictions and global news"
                        </p>
                    </div>
                    <div className="loading-state">
                        <p>Analyzing trending topics with AI...</p>
                    </div>
                </div>
            </section>
        );
    }

    if (state.error) {
        return (
            <section className="hot-topics-section">
                <div className="container">

                    
                    <div className="hot-topics-header">
                        <h2 className="section-title">
                            Hot Topics
                        </h2>
                    </div>
                    <div className="error-state">
                        <p className="error-message">{state.error}</p>
                        <button className="retry-button" onClick={fetchHotTopics}>
                            Try Again
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="hot-topics-section">
            <div className="container">
                
                <div className="hot-topics-header">
                    <h2 className="section-title">
                        Hot Topics
                    </h2>
                    <p className="section-subtitle">
                        {state.enhanced 
                            ? "Join the community to share your predictions and draw inspiration from AI summarized global news."
                            : "AI-powered trend analysis using advanced language models"
                        }
                    </p>
                    
                    <div className="status-indicators">
                        {state.enhanced && (
                            <div className="status-badge enhanced">
                                <span>Enhanced AI</span>
                            </div>
                        )}
                        
                        {state.communityMetrics.qualityFiltered && (
                            <div className="status-badge quality">
                                <span>Quality Filtered</span>
                            </div>
                        )}
                        
{state.communityMetrics.totalPredictions > 0 && (
    <div className="status-badge predictions">
        <span>{state.communityMetrics.totalPredictions} predictions analyzed</span>
    </div>
)}

{state.communityMetrics.totalEngagement > 0 && (
    <div className="status-badge engagement">
        <span>{Math.round(state.communityMetrics.totalEngagement)} total interactions</span>
    </div>
)}

{state.communityMetrics.activeUsers > 0 && (
    <div className="status-badge users">
        <span>{state.communityMetrics.activeUsers} contributor{state.communityMetrics.activeUsers !== 1 ? 's' : ''}</span>
    </div>
)}

                        
                        {state.communityMetrics.cacheHit && (
                            <div className="status-badge cache">
                                <span>Cached</span>
                            </div>
                        )}
                        
                        {state.newsMetrics.totalArticles && (
                            <div className="status-badge metrics">
                                <span>{state.newsMetrics.totalArticles} articles analyzed</span>
                            </div>
                        )}
                        
                        {state.lastUpdated && (
                            <div className="status-badge timestamp">
                                <span>Updated {formatTimeAgo(state.lastUpdated)}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="topics-grid">

                    {/* Box 1: Community Predictions */}
                    <div 
                        className="topic-box community-box"
                        onMouseEnter={handleCommunityMouseEnter}
                        onMouseLeave={handleCommunityMouseLeave}
                        onTouchStart={handleCommunityMouseEnter}
                        onTouchEnd={handleCommunityMouseLeave}
                    >
                        <div className="box-header">
                            <div className="box-title">
                                <h3>Community Predictions</h3>
                            </div>
                        </div>
                        
                        <div className="box-content">
                            {communityState.data.length > 0 ? (
                                <div className="content-slide" key={`community-${communityState.currentIndex}`}>
                                    <div className="content-main">
                                        <div className="content-header">
                                            <h4>{communityState.data[communityState.currentIndex]?.title}</h4>
                                        </div>
                                        
                                        <div className="content-description-wrapper">
                                            {/* ✅ NEW: Display bullet points if available */}
                                            {communityState.data[communityState.currentIndex]?.bulletPoints && 
                                             communityState.data[communityState.currentIndex].bulletPoints.length > 1 ? (
                                                <div className="bullet-points-container">
                                                    {communityState.data[communityState.currentIndex].bulletPoints.map((point, idx) => (
                                                        <div key={idx} className="bullet-point">
                                                            {addWordBreaks(ensureTextWrapping(point))}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
<>
    {/* ✅ NEW: Handle separate preview/full content */}
    {communityState.data[communityState.currentIndex]?.hasSeparateContent ? (
        <>
<p className="content-description">
    {communityState.data[communityState.currentIndex]?.fullAnalysis || 
     communityState.data[communityState.currentIndex]?.description}
</p>

            
{/* Read more button removed */}

        </>
    ) : (
        <>
<p className={`content-description ${communityState.data[communityState.currentIndex]?.hasAiSummary ? 'ai-enhanced' : ''}`}>
    {communityState.data[communityState.currentIndex]?.description}
</p>

{/* Read more button removed */}

        </>
    )}
</>

                                            )}
                                            
                                            {communityState.data[communityState.currentIndex]?.hasAiSummary && (
                                                <div className="ai-processing-badge">
                                                    <span>AI Processed</span>
                                                </div>
                                            )}
                                        </div>
                                        
                                    <div className="content-meta">
                                        {/* ✅ NEW: Add engagement metrics */}
<div className="engagement-stats">
    <button 
        className={`stat-button like-button ${userInteractions[communityState.data[communityState.currentIndex]?.id] === 'like' ? 'active' : ''}`}
        onClick={() => handleLike(communityState.data[communityState.currentIndex]?.id)}
        disabled={interactionLoading[communityState.data[communityState.currentIndex]?.id]}
    >
        <ThumbsUp size={16} />
        <span>{communityState.data[communityState.currentIndex]?.likes_count || 0}</span>
    </button>
    <button 
        className={`stat-button dislike-button ${userInteractions[communityState.data[communityState.currentIndex]?.id] === 'dislike' ? 'active' : ''}`}
        onClick={() => handleDislike(communityState.data[communityState.currentIndex]?.id)}
        disabled={interactionLoading[communityState.data[communityState.currentIndex]?.id]}
    >
        <ThumbsDown size={16} />
        <span>{communityState.data[communityState.currentIndex]?.dislikes_count || 0}</span>
    </button>
    <button 
        className="stat-button comment-button" 
        onClick={() => handleCommentToggle(communityState.data[communityState.currentIndex]?.id)}
    >
        <MessageCircle size={16} />
        <span>{communityState.data[communityState.currentIndex]?.comments_count || 0}</span>
    </button>
</div>


                                        
                                        {communityState.data[communityState.currentIndex]?.keywords?.length > 0 && (
                                            <div className="keywords">
                                                {communityState.data[communityState.currentIndex].keywords.slice(0, 4).map((keyword, idx) => (
                                                    <span key={idx} className="keyword-tag">{keyword}</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* ✅ NEW: Comment Section */}
                                    {expandedComments[communityState.data[communityState.currentIndex]?.id] && (
                                        <div className="comments-section">
                                            <div className="comments-header">
                                                <h4>Comments</h4>
                                            </div>
                                            
                                            <div className="comments-list scrollable-comments">
                                                {(communityState.data[communityState.currentIndex]?.comments || []).length > 0 ? (
                                                    (communityState.data[communityState.currentIndex]?.comments || []).map((comment, idx) => (
                                                        <div key={idx} className="comment-item">
                                                            <div className="comment-header">
                                                                <User size={14} />
                                                                <span className="comment-username">{comment.username}</span>
                                                                <span className="comment-time">{formatTimeAgo(comment.timestamp)}</span>
                                                            </div>
                                                            <p className="comment-text">{comment.comment_text}</p>                                                        </div>
                                                    ))
                                                ) : (
                                                    <p className="no-comments">No comments yet. Be the first to share your thoughts!</p>
                                                )}
                                            </div>
                                            
                                            <div className="comment-input-section">
                                                <div className="comment-input-wrapper">
                                                    <textarea
                                                        className="comment-input"
                                                        placeholder="Share your thoughts..."
                                                        value={commentText[communityState.data[communityState.currentIndex]?.id] || ''}
onChange={(e) => {
    setCommentText(prev => ({
        ...prev,
        [communityState.data[communityState.currentIndex]?.id]: e.target.value
    }));
    setIsTypingComment(prev => ({
        ...prev,
        [communityState.data[communityState.currentIndex]?.id]: true
    }));
}}
onBlur={() => {
    if (!commentText[communityState.data[communityState.currentIndex]?.id]?.trim()) {
        setIsTypingComment(prev => ({
            ...prev,
            [communityState.data[communityState.currentIndex]?.id]: false
        }));
    }
}}

                                                        rows={3}
                                                        maxLength={500}
                                                    />
                                                    <button
                                                        className="comment-submit-btn"
                                                        onClick={() => handleCommentSubmit(communityState.data[communityState.currentIndex]?.id)}
                                                        disabled={submittingComment[communityState.data[communityState.currentIndex]?.id] || 
                                                                 !commentText[communityState.data[communityState.currentIndex]?.id]?.trim()}
                                                    >
                                                        {submittingComment[communityState.data[communityState.currentIndex]?.id] ? (
                                                            'Posting...'
                                                        ) : (
                                                            <>
                                                                <Send size={16} />
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    </div>
                                </div>
                            ) : (
                                <div className="empty-content">
                                    <p>Loading community predictions...</p>
                                </div>
                            )}
                        </div>
                        
                        {communityState.data.length > 1 && (
                            <div className="box-controls">
                                <button 
                                    className="nav-button" 
                                    onClick={handleCommunityPrevious}
                                    aria-label="Previous community prediction"
                                >
                                    ‹
                                </button>
                                
                                <div className="dots-indicator">
                                    {communityState.data.map((_, index) => (
                                        <div 
                                            key={index} 
                                            className={`dot ${index === communityState.currentIndex ? 'active' : ''}`}
                                            onClick={() => handleCommunityDotClick(index)}
                                            style={{ cursor: 'pointer' }}
                                        />
                                    ))}
                                </div>
                                
                                <button 
                                    className="nav-button" 
                                    onClick={handleCommunityNext}
                                    aria-label="Next community prediction"
                                >
                                    ›
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ✅ UPDATED: Box 2: AI-Enhanced News Analysis with bullet points */}
                    <div 
                        className="topic-box news-box"
                        onMouseEnter={handleNewsMouseEnter}
                        onMouseLeave={handleNewsMouseLeave}
                        onTouchStart={handleNewsMouseEnter}
                        onTouchEnd={handleNewsMouseLeave}
                    >
                        <div className="box-header">
                            <div className="box-title">
                                <h3>AI News Analysis</h3>
                            </div>
                        </div>
                        
                        <div className="box-content">
                            {newsState.data.length > 0 ? (
                                <div className="content-slide" key={`news-${newsState.currentIndex}`}>
                                    <div className="content-main">
                                        <div className="content-header">
                                            <h4>{newsState.data[newsState.currentIndex]?.title}</h4>
                                        </div>
                                        
                                        <div className="content-description-wrapper">
                                            {/* ✅ NEW: Display bullet points if available */}
                                            {newsState.data[newsState.currentIndex]?.bulletPoints && 
                                             newsState.data[newsState.currentIndex].bulletPoints.length > 1 ? (
                                                <div className="bullet-points-container">
                                                    {newsState.data[newsState.currentIndex].bulletPoints.map((point, idx) => (
                                                        <div key={idx} className="bullet-point">
                                                            {addWordBreaks(ensureTextWrapping(point))}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <>
<p className="content-description">
    {newsState.data[newsState.currentIndex]?.description}
</p>

                                                    
{/* Read more button removed */}

                                                </>
                                            )}
                                        </div>
                                        
                                        <div className="content-meta">
                                            {newsState.data[newsState.currentIndex]?.keywords?.length > 0 && (
                                                <div className="keywords">
                                                    {newsState.data[newsState.currentIndex].keywords.slice(0, 4).map((keyword, idx) => (
                                                        <span key={idx} className="keyword-tag">{keyword}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="empty-content">
                                    <p>Loading AI news analysis...</p>
                                </div>
                            )}
                        </div>
                        
                        {newsState.data.length > 1 && (
                            <div className="box-controls">
                                <button 
                                    className="nav-button" 
                                    onClick={handleNewsPrevious}
                                    aria-label="Previous news analysis"
                                >
                                    ‹
                                </button>
                                
                                <div className="dots-indicator">
                                    {newsState.data.map((_, index) => (
                                        <div 
                                            key={index} 
                                            className={`dot ${index === newsState.currentIndex ? 'active' : ''}`}
                                            onClick={() => handleNewsDotClick(index)}
                                            style={{ cursor: 'pointer' }}
                                        />
                                    ))}
                                </div>
                                
                                <button 
                                    className="nav-button" 
                                    onClick={handleNewsNext}
                                    aria-label="Next news analysis"
                                >
                                    ›
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Login Modal */}
            {showLoginModal && (
                <LoginModal 
                    isOpen={showLoginModal} 
                    onClose={() => setShowLoginModal(false)} 
                />
            )}
        </section>
    );
});

HotTopics.displayName = 'HotTopics';

export default HotTopics;
